import { useEffect, useState } from 'react'
import type maplibregl from 'maplibre-gl'
import { graticuleFrame, type GraticuleFrame } from './graticule'
import { maxLinesFor, useGraticuleDensity } from './graticuleDensity'

const EMPTY: GraticuleFrame = { lon: [], lat: [], width: 0, height: 0 }

/** Координатная шкала: линейки по четырём краям и серая сетка.
 *
 *  Рисуется в DOM, а не слоем MapLibre. Причина простая: карта в кабинете без
 *  поворота (компас скрыт, вращение отключено в MapCanvas), поэтому меридианы
 *  всегда вертикальны, а параллели горизонтальны — линию можно поставить одним
 *  div'ом. Слой в MapLibre потребовал бы генерации геометрии, забот о порядке
 *  слоёв и подписи всё равно оказались бы внутри карты, а не по краям.
 *
 *  Шаг делений подбирается под масштаб (graticule.ts), поэтому сетка не густеет
 *  при отдалении и не исчезает при приближении. НАСКОЛЬКО она частая — выбирает
 *  судоводитель в настройках (graticuleDensity.ts); выбор задаёт потолок числа
 *  линий, а не шаг в градусах, и потому переживает зум.
 *
 *  Сам расчёт делений — в graticule.ts: тем же снимком рисуется сетка на
 *  скачиваемом кадре (exportImage), и разойтись им нельзя.
 */
export function MapGraticule({ map }: { map: maplibregl.Map | null }) {
  const [state, setState] = useState<GraticuleFrame>(EMPTY)
  const maxLines = maxLinesFor(useGraticuleDensity())

  useEffect(() => {
    if (!map) return
    let frame = 0

    const compute = () => {
      frame = 0
      setState(graticuleFrame(map, maxLines))
    }

    // move срабатывает десятки раз в секунду — считаем не чаще кадра.
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(compute)
    }

    compute()
    map.on('move', schedule)
    map.on('resize', schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      map.off('move', schedule)
      map.off('resize', schedule)
    }
  }, [map, maxLines])

  if (!state.lon.length && !state.lat.length) return null

  // Подписанные линии — опора взгляда, остальные бледнее: на густой сетке ровный
  // тон превратил бы её в штриховку, из которой не выхватить нужную параллель.
  // Оба класса выписаны целиком: Tailwind ищет их в исходнике как текст и
  // склеенный в рантайме `.../${…}` просто не соберёт.
  // Подписанная линия — ровно прежняя (/25): у того, кто густоту не трогал,
  // карта обязана выглядеть как вчера. Промежуточная вдвое бледнее — её видно,
  // но взгляд за неё не цепляется.
  const LINE_LABELLED = 'absolute bg-[rgb(var(--text-faint))]/25'
  const LINE_PLAIN = 'absolute bg-[rgb(var(--text-faint))]/20'
  const line = (labelled: boolean) => (labelled ? LINE_LABELLED : LINE_PLAIN)
  const label =
    'absolute rounded bg-[rgb(var(--surface-page))]/75 px-1 text-[10px] leading-tight text-mist tnum'

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {state.lon.map((t, i) => (
        <div key={`lon-${i}-${t.label}`}>
          <div
            className={line(t.labelled)}
            style={{ left: t.px, top: 0, width: 1, height: '100%' }}
          />
          {/* Подписи сверху и снизу: судоводитель читает ту линейку, что ближе. */}
          {t.labelled && (
            <>
              <div className={label} style={{ left: t.px + 3, top: 2 }}>
                {t.label}
              </div>
              <div className={label} style={{ left: t.px + 3, bottom: 2 }}>
                {t.label}
              </div>
            </>
          )}
        </div>
      ))}
      {state.lat.map((t, i) => (
        <div key={`lat-${i}-${t.label}`}>
          <div
            className={line(t.labelled)}
            style={{ top: t.px, left: 0, height: 1, width: '100%' }}
          />
          {t.labelled && (
            <>
              <div className={label} style={{ top: t.px + 2, left: 2 }}>
                {t.label}
              </div>
              <div className={label} style={{ top: t.px + 2, right: 2 }}>
                {t.label}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  )
}
